import type { ApiSuccess } from '@/lib/api'
import type { ImpersonateResult } from '../types'
import { http } from '@/lib/api'

/**
 * Super-admin impersonation (swagger/admin-auth.json).
 *
 * `start` is called with the admin token and returns an impersonation JWT
 * scoped to the target tenant, granting the union of the account's Admin +
 * Compliance Officer permissions. The frontend sends that token in place of the
 * admin token (see the axios interceptor) until `exit`, which MUST run with the
 * impersonation token to revoke the server-side session.
 *
 * **Lifetime (AMLIQ-218).** The JWT itself carries no `exp`, but it is backed by
 * a server-side session with a **sliding 60-minute inactivity window**: every
 * authenticated request made with the token extends it by another 60 minutes,
 * so normal usage keeps it alive by itself. There is no refresh endpoint. After
 * 60 minutes with no request the session lapses and cannot be revived — the
 * next call returns 401 and the interceptor drops back to the platform view.
 */
export const impersonationApi = {
  /** `POST /admin/auth/impersonate` — enter a tenant with admin access. */
  start: (tenantId: string): Promise<ImpersonateResult> =>
    http
      .post<ApiSuccess<ImpersonateResult>>('/admin/auth/impersonate', { tenantId })
      .then((res) => res.data),

  /** `POST /admin/auth/impersonate/exit` — end the current session (idempotent). */
  exit: (): Promise<void> => http.post<void>('/admin/auth/impersonate/exit'),
}
